const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const USER_KEY = 'user';
const TOKEN_EXPIRY_KEY = 'token_expiry';

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
}

function setCookie(name: string, value: string, maxAgeSec?: number): void {
  if (typeof document === 'undefined') return;
  let cookie = `${name}=${encodeURIComponent(value)}; path=/; SameSite=Strict`;
  if (maxAgeSec !== undefined) {
    cookie += `; max-age=${maxAgeSec}`;
  }
  if (process.env.NODE_ENV === 'production') {
    cookie += '; Secure';
  }
  document.cookie = cookie;
}

function deleteCookie(name: string): void {
  if (typeof document === 'undefined') return;
  let cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Strict`;
  if (process.env.NODE_ENV === 'production') {
    cookie += '; Secure';
  }
  document.cookie = cookie;
}

export function isAuthenticated(): boolean {
  const token = getAccessToken();
  return !!token && !isTokenExpired();
}

export function getAccessToken(): string | null {
  return getCookie(ACCESS_TOKEN_KEY);
}

export function setAccessToken(token: string, expiresIn?: number): void {
  setCookie(ACCESS_TOKEN_KEY, token, expiresIn);
}

export function removeAccessToken(): void {
  deleteCookie(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return getCookie(REFRESH_TOKEN_KEY);
}

export function setRefreshToken(token: string, maxAgeSec = 604800): void {
  setCookie(REFRESH_TOKEN_KEY, token, maxAgeSec);
}

export function removeRefreshToken(): void {
  deleteCookie(REFRESH_TOKEN_KEY);
}

import type { User } from '@/types/auth';

export function getUser(): User | null {
  const stored = getCookie(USER_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored) as User;
  } catch {
    return null;
  }
}

export function setUser(user: User): void {
  setCookie(USER_KEY, JSON.stringify(user), 604800);
}

export function removeUser(): void {
  deleteCookie(USER_KEY);
}

export function getTokenExpiry(): number | null {
  const stored = getCookie(TOKEN_EXPIRY_KEY);
  return stored ? parseInt(stored, 10) : null;
}

export function setTokenExpiry(expiry: number): void {
  setCookie(TOKEN_EXPIRY_KEY, expiry.toString(), 604800);
}

export function isTokenExpired(): boolean {
  const expiry = getTokenExpiry();
  if (!expiry) return true;
  return Date.now() >= expiry * 1000;
}

export function clearAuth(): void {
  removeAccessToken();
  removeRefreshToken();
  removeUser();
  deleteCookie(TOKEN_EXPIRY_KEY);
}
